import React, { useCallback, useEffect, useState } from "react";
import { logo } from "../../../commons/PanelImagepath";
import axios from "axios";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { Error, Success } from "../../../commons/Messages/Messages";
import Loading from "../../../commons/Loading/Loading";
import { Alert, Button, Descriptions, Modal, Space, Tag, Typography, theme } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import Title from "antd/es/typography/Title";
import {
  DOCUMENT_ONLY_TYPES,
  correctionReasonLabels,
  invoiceStatusMeta,
  invoiceTypeLabels,
  taxModeLabels,
  writeOffReasonLabels
} from "../../../utils/invoiceStatusArray";
import moment from "moment";
import { generalErrorAlert } from "../../GeneralErrorAlert";
import {
  currencySymbol,
  formatCents,
  formatDate
} from "../../../serviceProvider/components/accounting/shared/money";
import * as accounting from "../../../serviceProvider/components/accounting/receivables/api";

import { branchAddress, branchLabel, branchOfDoc } from "../customerBranches";
import { useTranslation } from "react-i18next";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import ActionSheet from "../../../design-system/ActionSheet";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { checkSpecificPermission } from "../../../utils/checkPermissions";
import { isAuthenticated } from "../../Auth/auth";
import AddUpdateInvoice from "./AddInvoices";
import CorrectionDialog from "./CorrectionDialog";
import SendDialog from "./SendDialog";
import PrintDialog from "./PrintDialog";
import CreditPrompt from "./CreditPrompt";
import DocumentChain from "./DocumentChain";
import { PaymentsList, RecordPaymentDialog, ReversePaymentDialog } from "./PaymentDialogs";
import { CorrectionNoticeDialog, StornoDialog, WriteOffDialog } from "./InvoiceActionDialogs";
import { canAccounting, invoiceErrorText } from "./invoiceErrors";
import "./Invoices.css";
import "./InvoiceDialogs.css";
import SiteDocsPanel from "../../../serviceProvider/components/siteDocs/SiteDocsPanel";
import { canUseSiteDocs } from "../../../serviceProvider/components/siteDocs/access";

const CORRECTABLE_TYPES = ["invoice", "partial", "final"];

const InvoiceDetails = () => {
  const { t } = useTranslation("dashboard");
  const { t: ta } = useTranslation("accounting");
  const { token } = theme.useToken();
  const params = useParams();
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [modal, modalContext] = Modal.useModal();
  const [data, setData] = useState({});
  const fmtMoney = cents => formatCents(cents, { currency: data?.currency });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Which dialog is open: correction | storno | notice | writeOff | payment | send | sendMail
  const [dialog, setDialog] = useState(null);
  const [reversing, setReversing] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [rev, setRev] = useState(0);
  // PAY-2: free lines for a Rechnungskorrektur opened from the payment dialog
  const [correctionPrefill, setCorrectionPrefill] = useState(null);
  // GS-5: the sent Rechnungskorrektur whose customer credit to ask about
  const [creditPromptFor, setCreditPromptFor] = useState(null);

  const canRead = canAccounting("Read");
  const canCreate = canAccounting("Create");
  const canUpdate = canAccounting("Update");
  const canEditDraft = checkSpecificPermission(
    isAuthenticated()?.roleId?.permissions,
    "Invoices",
    "Update"
  );

  const getData = useCallback(async () => {
    setLoading(true);
    await axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/invoices/invoice/${params?.id}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      })
      .then(res => {
        setLoading(false);
        if (res.status === 200 || res.status === 304) setData(res.data);
        else Error(res.data.errorMessage);
      })
      .catch(err => {
        setLoading(false);
        generalErrorAlert(err);
      });
  }, [params?.id]);

  useEffect(() => {
    getData();
    setDialog(null);
    setEvents([]);
    setPayments([]);
  }, [getData]);

  // Document chain and payments (accounting users only; customers see the document itself)
  useEffect(() => {
    if (!canRead || !params?.id) return;
    let alive = true;
    accounting
      .invoiceChain(params.id)
      .then(res => alive && setEvents(Array.isArray(res.data?.events) ? res.data.events : []))
      .catch(() => alive && setEvents([]));
    accounting
      .listInvoicePayments(params.id)
      .then(res => alive && setPayments(Array.isArray(res.data?.payments) ? res.data.payments : []))
      .catch(() => alive && setPayments([]));
    return () => {
      alive = false;
    };
  }, [params?.id, rev, canRead]);

  const refresh = () => {
    getData();
    setRev(r => r + 1);
  };

  // A correction draft that could not be sent right away opens with the send checklist.
  useEffect(() => {
    if (location.state?.openSend && data?._id === params?.id && !data?.lockedAt) {
      setDialog("sendMail");
      history.replace(location.pathname, {});
    }
    if (
      location.state?.creditPrompt &&
      data?._id === params?.id &&
      data?.lockedAt &&
      data?.type === "credit_note"
    ) {
      setCreditPromptFor(data._id);
      history.replace(location.pathname, {});
    }
  }, [location.state, data?._id]);

  const downloadXml = async ubl => {
    setBusy(true);
    try {
      await accounting.openBlob(
        accounting.invoiceXml(params.id, ubl ? { format: "ubl" } : undefined),
        `${data?.invoiceNumber || params.id}.${ubl ? "xrechnung" : "factur-x"}.xml`
      );
    } catch (error) {
      Error(accounting.errorText(error));
    }
    setBusy(false);
  };

  const sendInvoiceToClient = async () => {
    setBusy(true);
    await axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/invoices/send-to-client/${params.id}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      })
      .then(res => {
        if (res.status === 200 || res.status === 304) Success(res.data.successMessage);
        else Error(res.data.errorMessage);
      })
      .catch(err => generalErrorAlert(err));
    setBusy(false);
  };

  const waiveCharges = () =>
    modal.confirm({
      title: ta("Waive dunning charges"),
      content: ta(
        "The unpaid dunning fees and interest of this invoice are waived and reversed in the ledger."
      ),
      okText: ta("Waive dunning charges"),
      cancelText: ta("Cancel"),
      centered: true,
      onOk: async () => {
        try {
          await accounting.waiveCharges(params.id, {});
          Success(ta("Dunning charges waived"));
          refresh();
        } catch (error) {
          Error(invoiceErrorText(ta, error));
        }
      }
    });

  const closeDialog = changed => {
    setDialog(null);
    if (changed === true) refresh();
  };

  const locked = Boolean(data?.lockedAt);
  const hasCents = Boolean(data?.amounts?.byRate?.length);
  const meta = invoiceStatusMeta(data?.status);
  const legacyNet = parseFloat(data?.totalAmount || 0);
  const legacyRate = parseFloat(data?.tax || 0);
  const amounts = hasCents
    ? data.amounts
    : {
        net: Math.round(legacyNet * 100),
        tax: Math.round(legacyNet * legacyRate),
        gross: Math.round(legacyNet * (1 + legacyRate / 100) * 100),
        paid: Math.round(parseFloat(data?.partialPayment || 0) * 100),
        deductedNet: 0,
        deductedTax: 0
      };
  const open = hasCents ? amounts.open : amounts.gross - amounts.paid;
  const deductedGross = (amounts.deductedNet || 0) + (amounts.deductedTax || 0);
  const isFinal = data?.type === "final";
  const type = data?.type || "invoice";
  const docOnly = DOCUMENT_ONLY_TYPES.includes(type);
  const cancelled = meta.status === "cancelled";
  const writtenOff = Number(data?.amounts?.writtenOff || 0);
  const settledStatus = ["settled", "cancelled"].includes(meta.status);
  const dunning = data?.dunning || {};
  const chargesOutstanding = Math.max(
    0,
    Number(dunning.fees || 0) +
      Number(dunning.interest || 0) -
      Number(dunning.chargesPaid || 0) -
      Number(dunning.waived || 0)
  );
  const retention = data?.retention || {};
  const refDoc = data?.cancels;
  const refId = refDoc?._id || refDoc;
  const replacedEvent = events.find(e => e.kind === "replaced");
  const replacementEvent = events.find(e => e.kind === "replacement");
  const sendable = !locked && data?._id && canUpdate;

  // Actions: `main` ones stay visible on phones, the rest go into the action sheet.
  const actions = [];
  const add = (key, node, main = false) => actions.push({ key, node, main });
  if (!locked && data?._id && canEditDraft && !docOnly)
    add(
      "edit",
      <AddUpdateInvoice key="edit" data={data} isUpdate={true} updateParent={refresh} />,
      true
    );
  if (sendable) {
    add(
      "finalize",
      <Button key="finalize" loading={busy} onClick={() => setDialog("send")}>
        {ta("Finalize")}
      </Button>
    );
    add(
      "finalizeSend",
      <Button
        key="finalizeSend"
        type="primary"
        loading={busy}
        onClick={() => setDialog("sendMail")}
      >
        {ta("Finalize & send")}
      </Button>,
      true
    );
  }
  if (locked && !cancelled && canCreate && !docOnly && open > 0 && !settledStatus)
    add(
      "payment",
      <Button key="payment" type="primary" onClick={() => setDialog("payment")}>
        {ta("Record payment")}
      </Button>,
      true
    );
  add(
    "pdf",
    <Button key="pdf" onClick={() => setDialog("print")} loading={busy}>
      {ta("Download PDF")}
    </Button>,
    true
  );
  add(
    "mail",
    <Button key="mail" loading={busy} onClick={sendInvoiceToClient}>
      {t("Send Invoice to client")}
    </Button>
  );
  if (locked)
    add(
      "xml",
      <Button key="xml" onClick={() => downloadXml(false)} loading={busy}>
        {ta("Download XML")}
      </Button>
    );
  if (locked && data?.einvoice?.format === "xrechnung_ubl")
    add(
      "ubl",
      <Button key="ubl" onClick={() => downloadXml(true)} loading={busy}>
        {ta("XRechnung (UBL)")}
      </Button>
    );
  if (locked && !cancelled && canCreate && CORRECTABLE_TYPES.includes(type) && !writtenOff)
    add(
      "correction",
      <Button key="correction" onClick={() => setDialog("correction")}>
        {ta("Invoice correction")}
      </Button>
    );
  if (locked && !cancelled && canCreate && !["cancellation", "correction_notice"].includes(type))
    add(
      "notice",
      <Button key="notice" onClick={() => setDialog("notice")}>
        {ta("Correction notice")}
      </Button>
    );
  if (locked && !cancelled && canUpdate && !docOnly && open > 0 && !settledStatus)
    add(
      "writeOff",
      <Button key="writeOff" danger onClick={() => setDialog("writeOff")}>
        {ta("Write off receivable")}
      </Button>
    );
  if (locked && canUpdate && chargesOutstanding > 0)
    add(
      "waive",
      <Button key="waive" onClick={waiveCharges}>
        {ta("Waive dunning charges")}
      </Button>
    );
  if (locked && !cancelled && canUpdate && !["cancellation", "correction_notice"].includes(type))
    add(
      "storno",
      <Button key="storno" danger onClick={() => setDialog("storno")}>
        {ta("Cancel invoice (Storno)")}
      </Button>
    );
  const visible = isMobile ? actions.filter(a => a.main) : actions;
  const more = isMobile ? actions.filter(a => !a.main) : [];

  const noticeColumns = [
    { title: ta("Field"), dataIndex: "label", mobile: "title" },
    { title: ta("Previously"), dataIndex: "before" },
    { title: ta("Corrected"), dataIndex: "after" }
  ];

  const itemColumns = [
    { title: "#", key: "index", mobile: "hidden", render: (_, __, index) => index + 1 },
    { title: t("DESCRIPTION"), dataIndex: "description", mobile: "title" },
    { title: ta("Quantity"), dataIndex: "quantity", align: "right" },
    { title: t("Unit"), dataIndex: "unit" },
    {
      title: ta("Unit price"),
      key: "unitPrice",
      align: "right",
      render: (_, item) =>
        item?.unitPrice !== undefined
          ? fmtMoney(item.unitPrice)
          : `${item?.price} ${currencySymbol(data?.currency)}`
    },
    {
      title: ta("VAT"),
      key: "taxRate",
      align: "right",
      render: (_, item) =>
        item?.taxRate !== undefined ? `${item.taxRate} %` : `${data?.tax || 0} %`
    },
    {
      title: ta("Net"),
      key: "net",
      align: "right",
      render: (_, item) => (
        <strong style={{ whiteSpace: "nowrap" }}>
          {item?.net !== undefined
            ? fmtMoney(item.net)
            : `${(parseFloat(item?.price || 0) * parseFloat(item?.quantity || 0)).toFixed(2)} ${currencySymbol(data?.currency)}`}
        </strong>
      )
    }
  ];

  // The addressee as printed: the branch, or the head office with the branch as place of service
  const branch = branchOfDoc(data);
  const toBranch = Boolean(branch) && data?.billTo !== "head_office";
  const addressee = toBranch ? branch : data?.client;

  return (
    <div className="invoice-details-page">
      {modalContext}
      {loading && data?._id !== params?.id ? (
        <Loading />
      ) : (
        <div className="content container-fluid">
          <div className="invoice-container">
            <div className="printHeader invoice-actions">
              {visible.map(a => a.node)}
              {more.length ? (
                <Button icon={<MoreOutlined />} onClick={() => setMoreOpen(true)}>
                  {ta("More actions")}
                </Button>
              ) : null}
            </div>
            <ActionSheet open={moreOpen} onClose={() => setMoreOpen(false)} title={ta("Actions")}>
              {more.map(a => a.node)}
            </ActionSheet>
            {data?._id ? (
              <>
                <CorrectionDialog
                  open={dialog === "correction"}
                  invoice={data}
                  prefill={correctionPrefill}
                  onClose={changed => {
                    setCorrectionPrefill(null);
                    closeDialog(changed);
                  }}
                />
                <PrintDialog
                  open={dialog === "print"}
                  invoiceId={data._id}
                  fileName={data.invoiceNumber || `Entwurf-${data._id}`}
                  onClose={() => setDialog(null)}
                />
                <SendDialog
                  open={dialog === "send" || dialog === "sendMail"}
                  email={dialog === "sendMail"}
                  invoice={data}
                  onClose={() => setDialog(null)}
                  onSent={() => {
                    if (data?.type === "credit_note") setCreditPromptFor(data._id);
                    closeDialog(true);
                  }}
                />
                <StornoDialog
                  open={dialog === "storno"}
                  invoice={data}
                  onClose={() => setDialog(null)}
                  onDone={refresh}
                />
                <CorrectionNoticeDialog
                  open={dialog === "notice"}
                  invoice={data}
                  onClose={() => setDialog(null)}
                />
                <WriteOffDialog
                  open={dialog === "writeOff"}
                  invoice={data}
                  onClose={() => setDialog(null)}
                  onDone={() => closeDialog(true)}
                />
                <RecordPaymentDialog
                  open={dialog === "payment"}
                  invoice={data}
                  onClose={() => setDialog(null)}
                  onDone={() => closeDialog(true)}
                  canCorrect={CORRECTABLE_TYPES.includes(type) && !writtenOff && canCreate}
                  onCorrection={prefill => {
                    if (prefill.paid) refresh();
                    setCorrectionPrefill(prefill);
                    setDialog("correction");
                  }}
                />
                {creditPromptFor ? (
                  <CreditPrompt
                    correctionId={creditPromptFor}
                    customerId={data?.client?._id || data?.client}
                    onClose={() => setCreditPromptFor(null)}
                    onDone={() => setRev(r => r + 1)}
                  />
                ) : null}
                <ReversePaymentDialog
                  open={Boolean(reversing)}
                  payment={reversing}
                  onClose={() => setReversing(null)}
                  onDone={() => {
                    setReversing(null);
                    refresh();
                  }}
                />
              </>
            ) : null}
            <div className="invoice-alerts">
              {locked && !cancelled && !docOnly ? (
                <Alert
                  type="info"
                  showIcon
                  message={ta(
                    "This invoice was sent and is locked. Changes require a cancellation invoice or a credit note."
                  )}
                />
              ) : null}
              {cancelled ? (
                <Alert
                  type="error"
                  showIcon
                  message={
                    <span>
                      {ta("Cancelled by")}{" "}
                      {data?.cancelledBy?._id ? (
                        <Link to={`/invoices/details/${data.cancelledBy._id}`}>
                          {data.cancelledBy.invoiceNumber || ""}
                        </Link>
                      ) : null}
                    </span>
                  }
                />
              ) : null}
              {docOnly && refId ? (
                <Alert
                  type="info"
                  showIcon
                  message={
                    <span>
                      {ta("Refers to invoice")}{" "}
                      <Link to={`/invoices/details/${refId}`}>
                        {refDoc?.invoiceNumber || refDoc?.title || ta("Open")}
                      </Link>
                    </span>
                  }
                  description={
                    type === "credit_note" && data?.correctionReason?.code
                      ? `${ta("Reason")}: ${ta(correctionReasonLabels[data.correctionReason.code] || data.correctionReason.code)}${data.correctionReason.text ? ` – ${data.correctionReason.text}` : ""}`
                      : undefined
                  }
                />
              ) : null}
              {data?.replaces ? (
                <Alert
                  type="info"
                  showIcon
                  message={
                    <span>
                      {ta("Replaces the cancelled invoice")}{" "}
                      <Link to={`/invoices/details/${data.replaces?._id || data.replaces}`}>
                        {data.replaces?.invoiceNumber || replacedEvent?.number || ta("Open")}
                      </Link>
                    </span>
                  }
                />
              ) : null}
              {data?.replacedBy ? (
                <Alert
                  type="warning"
                  showIcon
                  message={
                    <span>
                      {ta("Replaced by")}{" "}
                      <Link to={`/invoices/details/${data.replacedBy?._id || data.replacedBy}`}>
                        {data.replacedBy?.invoiceNumber || replacementEvent?.number || ta("Draft")}
                      </Link>
                    </span>
                  }
                />
              ) : null}
              {writtenOff > 0 ? (
                <Alert
                  type="warning"
                  showIcon
                  message={ta("{{amount}} written off on {{date}}", {
                    amount: fmtMoney(writtenOff),
                    date: formatDate(data?.writeOff?.date)
                  })}
                  description={
                    data?.writeOff?.reason
                      ? `${ta(writeOffReasonLabels[data.writeOff.reason] || data.writeOff.reason)}${data.writeOff.note ? ` – ${data.writeOff.note}` : ""}`
                      : undefined
                  }
                />
              ) : null}
            </div>
            <div className="row">
              <div className="col-sm-6 mb-4">
                <img alt="Logo" className="inv-logo img-fluid" src={logo} />
              </div>
              <div className="col-sm-6 m-b-20">
                <div className="invoice-details">
                  <h3 className="text-uppercase">
                    {ta(invoiceTypeLabels[data?.type] || "Invoice")}{" "}
                    <small>{data?.invoiceNumber || data?.legacyNumber || ta("Draft")}</small>
                  </h3>
                  <ul className="list-unstyled mb-0">
                    <li>
                      {ta("Invoice date")}:{" "}
                      <span>
                        {data?.issueDate
                          ? formatDate(data.issueDate)
                          : moment(data?.createdAt).format("DD.MM.YYYY")}
                      </span>
                    </li>
                    <li>
                      {ta("Service date")}:{" "}
                      <span>{data?.serviceDate ? formatDate(data.serviceDate) : "–"}</span>
                    </li>
                    <li>
                      {t("Due Date:")}{" "}
                      <span>
                        {data?.dueDate ? moment(data?.dueDate).format("DD.MM.YYYY") : "–"}
                      </span>
                    </li>
                    <li>
                      {ta("Payment terms")}:{" "}
                      <span>
                        {data?.paymentTerms?.dueDays ?? 14} {ta("days")}
                        {data?.paymentTerms?.skontoPercent
                          ? `, ${data.paymentTerms.skontoPercent} % ${ta("discount")} / ${data.paymentTerms.skontoDays} ${ta("days")}`
                          : ""}
                      </span>
                    </li>
                    <li>
                      {ta("Tax mode")}:{" "}
                      <span>{ta(taxModeLabels[data?.taxMode] || taxModeLabels.regular)}</span>
                    </li>
                  </ul>
                  <div className="invoice-status">
                    <h3>{t("Status:")} </h3>
                    <Tag color={meta.tag} className="invoice-status-tag">
                      {ta(`status.${meta.status}`)}
                    </Tag>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12 mb-4">
                <ul className="list-unstyled mb-0">
                  <li>{data?.serviceProvider?.companyName || t("LeistFusion")}</li>
                  <li>{data?.serviceProvider?.address}</li>
                  <li>
                    {data?.serviceProvider?.zipCode} {data?.serviceProvider?.city},{" "}
                    {data?.serviceProvider?.country}
                  </li>
                </ul>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-6 col-lg-7 col-xl-8 mb-4">
                <h6>{t("Invoice to")}</h6>
                <ul className="list-unstyled mb-0">
                  <li>
                    <h5 className="mb-0">
                      <strong>{data?.client?.company}</strong>
                    </h5>
                    {toBranch ? (
                      <h5 className="mb-0">
                        <strong>{branch.name}</strong>
                      </h5>
                    ) : null}
                    <h5 className="mb-0">
                      <strong>
                        {toBranch
                          ? branch.contactName
                          : `${data?.client?.firstName || ""} ${data?.client?.lastName || ""}`}
                      </strong>
                    </h5>
                  </li>
                  <li>{addressee?.address}</li>
                  <li>
                    {addressee?.zipCode} {addressee?.city}
                  </li>
                  <li>{addressee?.country || data?.client?.country}</li>
                  <li>{(toBranch && branch.phone) || data?.client?.phone}</li>
                  <li>{(toBranch && branch.email) || data?.client?.email}</li>
                  {branch ? (
                    <li>
                      {ta("Branch")}: {branchLabel(branch)}
                    </li>
                  ) : null}
                  {branch && !toBranch ? (
                    <li>
                      {ta("Place of service")}: {branchAddress(branch)}
                    </li>
                  ) : null}
                  {data?.client?.customerNumber ? (
                    <li>
                      {ta("Customer number")}: {data.client.customerNumber}
                    </li>
                  ) : null}
                  {data?.client?.vatId ? <li>USt-IdNr.: {data.client.vatId}</li> : null}
                </ul>
              </div>
              <div className="col-sm-6 col-lg-5 col-xl-4 mb-4">
                <h6>{t("Payment Details")}</h6>
                <ul className="list-unstyled invoice-payment-details mb-0">
                  <li>
                    {t("Bank name:")} <span>{data?.serviceProvider?.bankDetails?.bankName}</span>
                  </li>
                  <li>
                    {t("IBAN:")} <span>{data?.serviceProvider?.bankDetails?.iban}</span>
                  </li>
                  <li>
                    {t("SWIFT code:")}{" "}
                    <span>
                      {data?.serviceProvider?.bankDetails?.bic ||
                        data?.serviceProvider?.bankDetails?.swiftCode}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            {type === "correction_notice" ? (
              <ResponsiveTable
                className="invoice-items"
                size="small"
                columns={noticeColumns}
                dataSource={data?.correctionNotice?.fields || []}
                rowKey={(f, index) => `${f?.label}-${index}`}
                pagination={false}
                mobileAvatar={false}
              />
            ) : (
              <ResponsiveTable
                className="invoice-items"
                size="small"
                columns={itemColumns}
                dataSource={data?.items || []}
                rowKey={(item, index) => item?._id ?? index}
                pagination={false}
              />
            )}
            <div className="row invoice-payment">
              <div className="col-md-6 mb-3">
                {isFinal && data?.relatedInvoices?.length ? (
                  <Descriptions
                    title={ta("Deducted advance invoices")}
                    size="small"
                    column={1}
                    bordered
                  >
                    {data.relatedInvoices.map(r => (
                      <Descriptions.Item
                        key={r._id}
                        label={`${r.invoiceNumber || r.title} (${formatDate(r.issueDate)})`}
                      >
                        - {fmtMoney(r.amounts?.gross || 0)}
                      </Descriptions.Item>
                    ))}
                  </Descriptions>
                ) : null}
              </div>
              <div className="col-md-6">
                <div className="table-responsive no-border">
                  <table className="table mb-0 invoice-totals">
                    <tbody>
                      {data?.amounts?.discountNet ? (
                        <tr>
                          <th>{ta("Invoice discount")}</th>
                          <td className="text-end">- {fmtMoney(data.amounts.discountNet)}</td>
                        </tr>
                      ) : null}
                      <tr>
                        <th>{t("Subtotal:")}</th>
                        <td className="text-end">{fmtMoney(amounts.net)}</td>
                      </tr>
                      {(hasCents ? amounts.byRate : [{ rate: legacyRate, tax: amounts.tax }]).map(
                        g => (
                          <tr key={g.rate}>
                            <th>
                              {t("Tax:")} <span className="text-regular">({g.rate} %)</span>
                            </th>
                            <td className="text-end">{fmtMoney(g.tax)}</td>
                          </tr>
                        )
                      )}
                      <tr>
                        <th>{t("Total:")}</th>
                        <td className="text-end text-primary">
                          <h5>{fmtMoney(amounts.gross)}</h5>
                        </td>
                      </tr>
                      {isFinal ? (
                        <tr>
                          <th>{ta("Less advance invoices")}</th>
                          <td className="text-end">- {fmtMoney(deductedGross)}</td>
                        </tr>
                      ) : null}
                      {retention.amount ? (
                        <tr>
                          <th>
                            {ta("Retention")}
                            {retention.dueDate ? (
                              <span className="text-regular">
                                {" "}
                                ({ta("due")} {formatDate(retention.dueDate)})
                              </span>
                            ) : null}
                            {retention.guarantee ? (
                              <Tag style={{ marginInlineStart: 6 }}>{ta("Guarantee")}</Tag>
                            ) : null}
                          </th>
                          <td className="text-end">{fmtMoney(retention.amount)}</td>
                        </tr>
                      ) : null}
                      <tr>
                        <th>{t("Paid:")}</th>
                        <td className="text-end text-primary">
                          <h5>{fmtMoney(amounts.paid || 0)}</h5>
                        </td>
                      </tr>
                      {data?.dunning?.fees || data?.dunning?.interest ? (
                        <tr>
                          <th>{ta("Dunning fees and interest")}</th>
                          <td className="text-end">
                            {fmtMoney((data.dunning.fees || 0) + (data.dunning.interest || 0))}
                          </td>
                        </tr>
                      ) : null}
                      <tr className="pt-2">
                        <th>
                          <Title level={5}>{t("Total Due:")}</Title>
                        </th>
                        <td className="text-end fw-bold text-danger">
                          <Title
                            level={5}
                            style={{ color: open > 0 ? token.colorError : token.colorSuccess }}
                          >
                            {fmtMoney(Math.max(0, open))}
                          </Title>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="invoice-info">
              <h5>{t("Other information")}</h5>
              {data?.headerText ? <p className="mb-1">{data.headerText}</p> : null}
              <p className="text-muted mb-0">{data?.description}</p>
              {data?.footerText ? <p className="text-muted mb-0 mt-1">{data.footerText}</p> : null}
              {data?.directDebit?.enabled || data?.smallAmountInvoice ? (
                <Space wrap style={{ marginTop: 8 }}>
                  {data?.directDebit?.enabled ? (
                    <Tag color="blue">{ta("Collected by direct debit")}</Tag>
                  ) : null}
                  {data?.smallAmountInvoice ? <Tag>{ta("Small-amount invoice")}</Tag> : null}
                </Space>
              ) : null}
              {locked ? (
                <Space wrap style={{ marginTop: 8 }}>
                  <Tag>
                    {ta("E-invoice")}:{" "}
                    {data?.einvoice?.format === "xrechnung_ubl"
                      ? "XRechnung (UBL) + ZUGFeRD"
                      : "ZUGFeRD 2.3 (EN 16931)"}
                  </Tag>
                  {data?.einvoice?.validatedAt ? (
                    <Tag color="green">
                      {ta("Validated")} {formatDate(data.einvoice.validatedAt)}
                    </Tag>
                  ) : null}
                  {data?.journalEntry ? (
                    <Tag color="blue">
                      {ta("Journal entry")}{" "}
                      {data.journalEntry?.entryNo
                        ? `${data.journalEntry.entryNo}/${data.journalEntry.year}`
                        : ""}
                    </Tag>
                  ) : null}
                </Space>
              ) : null}
            </div>
            {canRead && data?._id && (locked || events.length > 0) ? (
              <div className="invoice-section">
                <div className="invoice-section__head">
                  <Typography.Title level={5}>{ta("Document history")}</Typography.Title>
                </div>
                <DocumentChain events={events} currentId={data._id} />
              </div>
            ) : null}
            {canRead && locked && !docOnly ? (
              <div className="invoice-section" id="invoice-payments">
                <div className="invoice-section__head">
                  <Typography.Title level={5}>{ta("Payments")}</Typography.Title>
                  {canCreate && !cancelled && open > 0 && !settledStatus ? (
                    <Button onClick={() => setDialog("payment")}>{ta("Record payment")}</Button>
                  ) : null}
                </div>
                <PaymentsList
                  payments={payments}
                  canReverse={canUpdate}
                  onReverse={p => setReversing(p)}
                />
              </div>
            ) : null}
            {data?._id && !docOnly && canUseSiteDocs() ? (
              <div className="invoice-section" id="invoice-site-docs">
                <div className="invoice-section__head">
                  <Typography.Title level={5}>{t("Documentation")}</Typography.Title>
                </div>
                <SiteDocsPanel
                  filter={{ invoice: data._id }}
                  client={data.client?._id || data.client || ""}
                  customerEmail={data.client?.email || ""}
                  reportTitle={data.title || ""}
                />
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetails;
